import { createTheme } from '@mui/material/styles';

export const createAppTheme = (mode = 'dark') => {
  // Ensure mode is valid
  const validMode = (mode === 'light' || mode === 'dark') ? mode : 'dark';
  const isDark = validMode === 'dark';
  
  return createTheme({
    palette: {
      mode: validMode,
      primary: {
        main: isDark ? '#667eea' : '#5a67d8',
        light: isDark ? '#9bb5ff' : '#7c3aed',
        dark: isDark ? '#3f51b5' : '#4c1d95',
      },
      secondary: {
        main: isDark ? '#764ba2' : '#6b46c1',
        light: isDark ? '#a777d3' : '#8b5cf6',
        dark: isDark ? '#512da8' : '#4c1d95',
      },
      background: {
        default: isDark ? '#0f0f23' : '#f8fafc',
        paper: isDark ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
      },
      action: {
        hover: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
      },
      text: {
        primary: isDark ? '#ffffff' : '#000000',
        secondary: isDark ? 'rgba(255, 255, 255, 0.7)' : '#666666',
      },
      success: {
        main: isDark ? '#43e97b' : '#10b981',
        light: isDark ? '#6ee7a3' : '#34d399',
        dark: isDark ? '#2dd4aa' : '#059669',
      },
      warning: {
        main: isDark ? '#f093fb' : '#f59e0b',
        light: isDark ? '#f4a5fc' : '#fbbf24',
        dark: isDark ? '#ec7df8' : '#d97706',
      },
      error: {
        main: isDark ? '#ff6b6b' : '#ef4444',
        light: isDark ? '#ff8a8a' : '#f87171',
        dark: isDark ? '#ff4c4c' : '#dc2626',
      },
      info: {
        main: isDark ? '#4facfe' : '#3b82f6',
        light: isDark ? '#6fc0ff' : '#60a5fa',
        dark: isDark ? '#2e98fd' : '#2563eb',
      },
      divider: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
      },
      h2: {
        fontWeight: 600,
      },
      h3: {
        fontWeight: 600,
      },
      h4: {
        fontWeight: 600,
      },
      h5: {
        fontWeight: 500,
      },
      h6: {
        fontWeight: 500,
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 12,
            backdropFilter: 'blur(10px)',
            backgroundColor: theme.palette.mode === 'light' 
              ? '#ffffff !important' 
              : 'rgba(255, 255, 255, 0.05) !important',
            border: theme.palette.mode === 'light' 
              ? '1px solid #d1d5db' 
              : '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: theme.palette.mode === 'light' 
              ? '0 4px 6px -1px rgba(0, 0, 0, 0.3) !important'
              : '0 4px 6px -1px rgba(0, 0, 0, 0.5) !important',
          }),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 500,
          },
          outlined: ({ theme }) => ({
            borderWidth: '1.5px',
            '&:hover': {
              borderWidth: '1.5px',
              backgroundColor: theme.palette.mode === 'light' 
                ? 'rgba(0, 0, 0, 0.04)' 
                : 'rgba(255, 255, 255, 0.08)',
            },
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 16,
            border: theme.palette.mode === 'light' ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
          }),
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 8,
            border: theme.palette.mode === 'light' ? '1px solid' : 'none',
          }),
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
            backdropFilter: 'blur(10px)',
          }),
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: ({ theme }) => ({
            '& .MuiTab-root': {
              color: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.7) !important' 
                : '#000000 !important',
              '&.Mui-selected': {
                color: theme.palette.mode === 'dark' 
                  ? '#ffffff !important' 
                  : '#000000 !important',
              },
              '& .MuiSvgIcon-root': {
                color: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.7) !important' 
                  : '#000000 !important',
              },
              '&.Mui-selected .MuiSvgIcon-root': {
                color: theme.palette.mode === 'dark' 
                  ? '#ffffff !important' 
                  : '#000000 !important',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }),
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.mode === 'light' ? '#000000 !important' : '#ffffff !important',
          }),
          h1: ({ theme }) => ({
            color: theme.palette.mode === 'light' ? '#000000 !important' : '#ffffff !important',
            fontWeight: 700,
          }),
          h2: ({ theme }) => ({
            color: theme.palette.mode === 'light' ? '#000000 !important' : '#ffffff !important',
            fontWeight: 600,
          }),
          h3: ({ theme }) => ({
            color: theme.palette.mode === 'light' ? '#000000 !important' : '#ffffff !important',
            fontWeight: 600,
          }),
          h4: ({ theme }) => ({
            color: theme.palette.mode === 'light' ? '#000000 !important' : '#ffffff !important',
            fontWeight: 600,
          }),
          h5: ({ theme }) => ({
            color: theme.palette.mode === 'light' ? '#000000 !important' : '#ffffff !important',
            fontWeight: 500,
          }),
          h6: ({ theme }) => ({
            color: theme.palette.mode === 'light' ? '#000000 !important' : '#ffffff !important',
            fontWeight: 500,
          }),
          body1: ({ theme }) => ({
            color: theme.palette.mode === 'light' ? '#000000 !important' : '#ffffff !important',
          }),
          body2: ({ theme }) => ({
            color: theme.palette.mode === 'light' ? '#666666 !important' : 'rgba(255, 255, 255, 0.7) !important',
          }),
          caption: ({ theme }) => ({
            color: theme.palette.mode === 'light' ? '#666666 !important' : 'rgba(255, 255, 255, 0.7) !important',
          }),
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: ({ theme }) => {
            if (!theme || !theme.palette) {
              return {
                color: '#ffffff !important',
              };
            }
            return {
              color: theme.palette.mode === 'light' ? '#000000 !important' : '#ffffff !important',
            };
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => {
            if (!theme || !theme.palette) {
              return {
                color: '#ffffff !important',
                '& .MuiSvgIcon-root': {
                  color: '#ffffff !important',
                },
              };
            }
            return {
              color: theme.palette.mode === 'light' ? '#000000 !important' : '#ffffff !important',
              '& .MuiSvgIcon-root': {
                color: theme.palette.mode === 'light' ? '#000000 !important' : '#ffffff !important',
              },
            };
          },
        },
      },
    },
  });
};
